<template>
  <div
    js-media-slideshow="parent"
    class="media-slideshow critical-component-clear"
    :style="`background-color: ${sectionSettings.backgroundColor};
    --padding-bottom-mobile: ${sectionSettings.paddingBottomMobile}px;
    --padding-top-mobile: ${sectionSettings.paddingTopMobile}px;
    --padding-bottom-desktop: ${sectionSettings.paddingBottomDesktop}px;
    --padding-top-desktop: ${sectionSettings.paddingTopDesktop}px;
    `"
    :class="[
      {
        [$class('hidden')]: isMobile
          ? sectionSettings.hideMobile
          : sectionSettings.hideDesktop,
      },
    ]"
  >
    <div
      v-if="slides"
      ref="container"
      class="media-slideshow__container"
    >
      <div
        ref="content"
        class="media-slideshow__content"
      >
        <template
          v-for="(block, index) of slides"
          :key="`media-slideshow-${block.blockId}-${index}`"
        >
          <div
            class="media-slideshow__slide"
            :class="[{
                       [$class('hidden')]: isMobile
                         ? block.mediaSectionSettings.sectionSettings.hideMobile
                         : block.mediaSectionSettings.sectionSettings.hideDesktop,
                     },
                     // eslint-disable-next-line max-len
                     `media-slideshow__slide--columns-desktop-${block.mediaSectionSettings.desktopMediaSettings.desktopLayout}`,
                     // eslint-disable-next-line max-len
                     `media-slideshow__slide--layout-mobile-${block.mediaSectionSettings.mobileMediaSettings.mobileLayout}`,
            ]"
            :data-image-index="index"
          >
            <div
              class="media-slideshow__slide-content"
              @click="handleImageClick(index)"
            >
              <media-section
                :section-settings="block.mediaSectionSettings.sectionSettings"
                :mobile-media-settings="block.mediaSectionSettings.mobileMediaSettings"
                :desktop-media-settings="block.mediaSectionSettings.desktopMediaSettings"
                :content-type="block.blockType"
                content-placement="half"
                :show-content="false"
              />

              <h2
                class="media-slideshow__slide-title text-mobile-12-mobile text-desktop-12-desktop"
              >
                <span v-text="block.mediaSectionSettings.sectionSettings.titleText" />
                <icon-chevron-right-special
                  v-if="block.mediaSectionSettings.sectionSettings.titleText"
                />
              </h2>
            </div>

            <button
              v-if="isMobile && block.mediaSectionSettings.linkSettings[0]?.title"
              class="media-slideshow__cta media-slideshow__cta--open"
              type="button"
              @click="handleViewProductsClick(block.mediaSectionSettings.linkSettings)"
            >
              <icon-plus-new />
              <span
                class="
                text-desktop-12-desktop
                text-mobile-12-mobile
                text-regular"
                v-text="$string('general.sections.view_products')"
              />
            </button>

            <ul
              v-else-if="!isMobile && block.mediaSectionSettings.linkSettings"
              class="media-slideshow__slide-footer text-mobile-12-mobile text-desktop-12"
            >
              <template
                v-for="(link, index) of block.mediaSectionSettings.linkSettings"
                :key="`media-content__link-${index}`"
              >
                <li
                  v-if="link.url || link.title"
                  class="media-slideshow__link"
                >
                  <a

                    class="text-desktop-12 text-mobile-12-mobile text-regular"
                    :href="link.url"
                    v-text="link.title"
                  />

                  <template
                    v-if="
                      link.productHandle &&
                        products.find(product => product.handle === link.productHandle)
                    "
                  >
                    <div
                      class="media-slideshow__product-image"
                    >
                      <responsive-image
                        :image="getProductImageByHandle(link.productHandle)"
                        min-max="780-2000"
                        sizes="(max-width: 767px) 100vw, (min-width: 768px) calc(100vw / 4)"
                        ratio="5:7"
                        :padding="false"
                      />
                    </div>
                  </template>
                </li>
              </template>
            </ul>
          </div>
        </template>
      </div>

      <overlay
        v-if="isMobile"
        key="mediaSlideshowProducts"
        direction="up"
        :namespace="sectionSettings.id"
        :template="false"
        type="shelf"
      >
        <template #body="{ isActive }">
          <div
            class="media-slideshow__overlay-container"
            :is-active="isActive"
            js-body-scroll-lock-ignore
          >
            <button
              class="media-slideshow__overlay-button media-slideshow__cta"
              type="button"
              @click="closeOverlay(sectionSettings.id)"
            >
              <icon-minus-new />
              <span
                class="
                text-desktop-12-desktop
                text-mobile-12-mobile
                text-regular"
                v-text="$string('general.sections.hide_products')"
              />
            </button>

            <ul
              class="media-slideshow__product-list"
            >
              <template v-if="products">
                <li
                  v-for="(product, index) of products"
                  :key="`media-slideshow__product-item-${index}`"
                  class="media-slideshow__product-item"
                  :class="activeProductHandles.includes(product.handle) &&
                    'media-slideshow__product-item--is-visible'"
                  :style="
                    `--product-order: ${activeProductHandles.indexOf(product.handle)};`"
                >
                  <a
                    class="text-mobile-12-mobile"
                    :href="product.url"
                    :aria-label="product.title"
                  >
                    <responsive-image
                      :image="getProductImageByHandle(product.handle)"
                      min-max="780-2000"
                      sizes="(max-width: 767px) 100vw, (min-width: 768px) calc(100vw / 4)"
                      ratio="5:7"
                      :padding="false"
                    />
                  </a>
                </li>
              </template>
              <template v-else>
                <li
                  v-for="index of 3"
                  :key="index"
                  class="media-slideshow__product-item media-slideshow__product-item--placeholder"
                />
              </template>
            </ul>
          </div>
        </template>
      </overlay>

      <secondary-product-data
        context="media-slideshow"
        :product-handles="getProductHandles()"
        @products-updated="handleProductsUpdate"
      />
    </div>

    <template v-if="!isMobile">
      <div
        class="media-slideshow__gallery-container"
        :class="galleryExpanded && 'media-slideshow__gallery-container--is-open'"
      >
        <ul
          class="media-slideshow__gallery"
          @click="handleGalleryClick"
        >
          <template
            v-for="(block, index) of slides"
            :key="index"
          >
            <template v-if="!block.mediaSectionSettings.sectionSettings.hideDesktop">
              <li
                v-if="block.mediaSectionSettings.mobileMediaSettings?.desktopVideoFile"
                ref="imageIndex"
                class="
                    media-slideshow__gallery-item
                    media-slideshow__gallery-item--video
                  "
              >
                <span
                  class="visually-hidden"
                  v-text="image.alt || $string('accessibility.image_placeholder')"
                />
                <video-player
                  :src="block.mediaSectionSettings.desktopMediaSettings.desktopVideoFile.length
                    ? block.mediaSectionSettings.desktopMediaSettings.desktopVideoFile :
                      block.mediaSectionSettings.mobileMediaSettings.mobileVideoFile"
                  :poster="block.mediaSectionSettings.desktopMediaSettings.desktopVideoFile.length
                    // eslint-disable-next-line max-len
                    ? block.mediaSectionSettings.desktopMediaSettings.desktopVideoFile.preview_image?.src :
                      // eslint-disable-next-line max-len
                      block.mediaSectionSettings.mobileMediaSettings.mobileVideoFile.preview_image?.src"
                  autoplay
                  :controls="false"
                />
              </li>
              <li
                v-else
                ref="imageIndex"
                class="media-slideshow__gallery-item"
                :style="`--desktop-aspect-ratio: ${getAspectRatio(block)}`"
              >
                <responsive-image
                  :image="
                    block.mediaSectionSettings.desktopMediaSettings.desktopImage.length
                      ?
                        block.mediaSectionSettings.desktopMediaSettings.desktopImage
                      :
                        block.mediaSectionSettings.mobileMediaSettings.mobileImage"
                  min-max="2100-4800"
                  ratio="image"
                  :padding="false"
                />
              </li>
            </template>
          </template>
        </ul>
      </div>
    </template>
  </div>
</template>

<script>

/**
 * Vue: Media Slideshow (media-slideshow)
 * -----------------------------------------------------------------------------
 * Media Slideshow.
 *
 * @param {String} liquid.sectionId - Section's Liquid ID.
 *
 */
import MediaSection from '~async/media-section/media-section.vue'
import ResponsiveImage from '~global/images/responsive-image'
import VideoPlayer from '~async/video-player/video-player'
import SecondaryProductData from '~async/main-product/secondary-product-data'
import Overlay from '~global/overlay/overlay'
import IconChevronRightSpecial from '~icons/direction/chevron/right-special.svg'
import IconPlusNew from '~icons/misc/plus-new.svg'
import IconMinusNew from '~icons/misc/minus-new.svg'
import { clearAllBodyScrollLocks, disableBodyScroll } from 'body-scroll-lock'
import { mapActions, mapState } from 'vuex'

export default {
  name: 'MediaSlideshow',
  components: {
    MediaSection,
    ResponsiveImage,
    VideoPlayer,
    SecondaryProductData,
    IconChevronRightSpecial,
    IconPlusNew,
    IconMinusNew,
    Overlay,
  },

  props: {
    sectionSettings: {
      type: Object,
      default: () => ({
        id: '',
        backgroundColor: '',
        hideDesktop: '',
        hideMobile: '',
      }),
    },
    slides: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      galleryExpanded: false,
      clickedImage: false,
      products: [],
      activeProductHandles: [],
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),
  },

  watch: {

    /**
     * When the galleryExpanded state changes play or reverse the gsap
     * animation.
     */
    galleryExpanded(value) {
      const self = this

      this.$emit('gallery-state-changed', value)

      if (!value) {
        clearAllBodyScrollLocks()

        return
      }
      disableBodyScroll(document.querySelector('.media-slideshow__gallery-container'))

      setTimeout(() => {
        self.clickedImage.scrollIntoView(true)
      }, 250)
    },
  },

  mounted() {
    this.setContainerHeight()
    this.setSlideIntersectionObservers()
    window.addEventListener('scroll', this.handleScroll)
    window.addEventListener('resize', this.handleResize)
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      closeOverlay: 'overlays/close',
      openOverlay: 'overlays/open',
    }),

    /**
     * Set container height based on scroll width
     */
    setContainerHeight() {
      if (!this.$refs.container) {
        return
      }

      if (window.innerWidth <= 1024) {
        this.$refs.container.style.height = `auto`
      } else {
        this.$refs.container.style.height = `${this.$refs.content.scrollWidth - (window.innerHeight / 2)}px`
      }
    },

    /**
     * Scroll content horizontally when scrolling vertically
     */
    handleScroll() {
      if (!this.$refs.container || this.isMobile) {
        return
      }

      const containerRect = this.$refs.container?.getBoundingClientRect()

      if (containerRect?.top <= 0 && containerRect?.bottom >= window?.innerHeight) {
        this.$refs.content.scrollLeft = Math.abs(containerRect.top)
      } else if (containerRect.top > 1 && this.$refs.content.scrollLeft !== 0) {
        this.$refs.content.scrollLeft = 0
      }
    },

    /**
     * Recalculate container height on resize
     */
    handleResize() {
      if (!this.$refs.container) {
        return
      }

      this.setContainerHeight()
    },

    /**
     * Handles click events on the gallery images.
     * @param {Number} index - Index in the for loop of the clicked image.
     */
    handleImageClick(index) {
      this.galleryExpanded = !this.galleryExpanded
      this.selectedImage = index

      const element = this.$refs.imageIndex[index]

      this.clickedImage = element

    },

    /**
     * Handles click events on the gallery images.
     * @param {Number} index - Index in the for loop of the clicked image.
     */
    handleGalleryClick() {
      this.galleryExpanded = false

    },

    handleViewProductsClick(links) {
      this.activeProductHandles = links.map(link => link.productHandle)

      this.openOverlay({
        ignoreDismissed: true,
        namespace: this.sectionSettings.id,
      })
    },

    /**
     * Returns the aspect ratio of the block image.
     * @param {Objet} block
     */
    getAspectRatio(block) {
      const image = block.mediaSectionSettings.desktopMediaSettings.desktopImage?.src
        ? block.mediaSectionSettings.desktopMediaSettings.desktopImage
        : block.mediaSectionSettings.mobileMediaSettings.mobileImage

      return `${image.width} / ${image.height}`
    },

    /**
     * Fetches product handles from link list.
     */
    getProductHandles() {
      const productHandles = []

      this.slides.forEach((slide) => {
        slide.mediaSectionSettings.linkSettings.forEach(link => {
          const productHandle = link.productHandle

          if (!productHandles.includes(productHandle)) {
            productHandles.push(productHandle)
          }
        })
      })

      return productHandles
    },

    /**
     * Returns the product object from the product handle.
     * @param {String} productHandle - Product handle.
     */
    getProductImageByHandle(productHandle) {
      const imageProduct = this.products?.find(product => product.handle === productHandle)

      const productImages = imageProduct.images

      let defaultImage = imageProduct.featuredImage || productImages[0]

      productImages?.forEach(image => {
        if (!image.alt) {
          return
        }

        const imageTags = image.alt.split('||')

        imageTags.forEach(tag => {

          // Returns flat image if product card is set to show flat image or
          // if current layout is set as overview
          if (tag.includes('flat_image')) {
            defaultImage = image
          }
        })
      })

      return defaultImage

    },

    /**
     * Gets the currently viewed image to set as the current index.
     */
    setSlideIntersectionObservers() {
      if (!this.$refs.container || !this.isMobile) {
        return
      }

      const slides = this.$refs.container.querySelectorAll('.media-slideshow__slide')

      const observerOptions = {
        rootMargin: '0% -50% 0% -50%',
      }

      const observer = new IntersectionObserver(observerCallback, observerOptions)

      function observerCallback(entries) {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('media-slideshow__slide--is-active')
          } else {
            entry.target.classList.remove('media-slideshow__slide--is-active')
          }

        })
      }

      slides.forEach(slide => {
        observer.observe(slide)
      })
    },

    /**
     * Handles products being updated by the secondary product data component.
     * @param {Array} products - Array of product objects.
     */
    handleProductsUpdate(products) {
      if (!products) {
        this.products = []
        return
      }

      const uniqueProducts = products.filter((product) => {
        return !this.products.some((existingProduct) => {
          return existingProduct.handle === product.handle
        })
      })

      this.products = this.products.concat(...uniqueProducts)
    },
  },
}
</script>

<style lang="scss">
@import './media-slideshow';
</style>
